import React from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Colors } from '@/constants/theme';

interface ConfirmModalProps {
    visible: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Styles the confirm button red for irreversible actions. */
    destructive?: boolean;
    /** Shows a spinner on the confirm button and blocks both buttons. */
    loading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export function ConfirmModal({
    visible,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    loading = false,
    onConfirm,
    onCancel,
}: ConfirmModalProps) {
    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
            <View style={styles.container}>
                <TouchableOpacity
                    style={[StyleSheet.absoluteFill, styles.backdrop]}
                    activeOpacity={1}
                    onPress={loading ? undefined : onCancel}
                />
                <View style={styles.card}>
                    <Text style={styles.title}>{title}</Text>
                    <Text style={styles.message}>{message}</Text>
                    <View style={styles.actions}>
                        <TouchableOpacity
                            style={[styles.button, styles.cancelButton]}
                            activeOpacity={0.8}
                            onPress={onCancel}
                            disabled={loading}
                        >
                            <Text style={styles.cancelButtonText}>{cancelLabel}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.button, destructive ? styles.destructiveButton : styles.confirmButton]}
                            activeOpacity={0.8}
                            onPress={onConfirm}
                            disabled={loading}
                        >
                            {loading
                                ? <ActivityIndicator size="small" color="#fff" />
                                : <Text style={styles.confirmButtonText}>{confirmLabel}</Text>}
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    backdrop: {
        backgroundColor: 'rgba(0,0,0,0.45)',
    },
    card: {
        width: '100%',
        maxWidth: 400,
        backgroundColor: '#fff',
        borderRadius: 16,
        padding: 24,
    },
    title: {
        fontSize: 17,
        fontWeight: '700',
        color: '#1A1A1A',
    },
    message: {
        fontSize: 14,
        color: '#555',
        marginTop: 10,
        lineHeight: 20,
    },
    actions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 10,
        marginTop: 24,
    },
    button: {
        minWidth: 110,
        borderRadius: 10,
        paddingVertical: 12,
        paddingHorizontal: 18,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#F2F2F2',
    },
    cancelButtonText: {
        color: '#1A1A1A',
        fontSize: 15,
        fontWeight: '600',
    },
    confirmButton: {
        backgroundColor: Colors.brand,
    },
    destructiveButton: {
        backgroundColor: '#E53935',
    },
    confirmButtonText: {
        color: '#fff',
        fontSize: 15,
        fontWeight: '600',
    },
});
